import {ReactNode, useEffect} from 'react';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import Analytics from 'utils/Analytics';
import {readSeverityFromUrl} from 'surveys/osa_funnel/survey/severity';
import {useGlp1Modality} from 'surveys/osa_funnel/hooks/useGlp1Modality';
import styles from './PrimerShell.module.css';

/** Render a headline with an inline-violet accent phrase. */
export const renderHeadline = (text: string, accentPhrase?: string): ReactNode => {
  if (!accentPhrase) return text;
  const idx = text.indexOf(accentPhrase);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className={styles.headlineAccent}>{text.slice(idx, idx + accentPhrase.length)}</span>
      {text.slice(idx + accentPhrase.length)}
    </>
  );
};

type PrimerSlideProps = {
  letter: 'A' | 'B' | 'C' | 'E';
  headline: string;
  subhead?: string;
  accentPhrase?: string;
  children?: ReactNode;
  ctaLabel?: string;
  /** Disable the CTA — used by filter slides until an option is picked. */
  ctaDisabled?: boolean;
  /** Optional override; defaults to surveyInstance.nextPage(). */
  onCta?: () => void;
};

/** Shared shell for the primer slides. Renders inline in the survey
 *  shell — the survey's own progress bar + back button serve as the
 *  chrome (no separate slide dots). */
export const PrimerSlide = ({
  letter,
  headline,
  subhead,
  accentPhrase,
  children,
  ctaLabel = 'Next',
  ctaDisabled,
  onCta,
}: PrimerSlideProps) => {
  const modality = useGlp1Modality();

  useEffect(() => {
    Analytics.track('GLP-1 Funnel Primer Card Viewed', {
      letter,
      severity: readSeverityFromUrl(),
      variant: modality,
    });
    // Snap to top so the headline lands in view. Double rAF to beat
    // SurveyJS's post-render scroll side-effects.
    requestAnimationFrame(() => {
      window.scrollTo({top: 0, behavior: 'auto'});
      requestAnimationFrame(() => window.scrollTo({top: 0, behavior: 'auto'}));
    });
  }, [letter, modality]);

  const handleCta = () => {
    if (onCta) {
      onCta();
      return;
    }
    if (surveyInstance.isLastPage) {
      surveyInstance.completeLastPage();
    } else {
      surveyInstance.nextPage();
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.slide}>
        <h2 className={styles.headline}>{renderHeadline(headline, accentPhrase)}</h2>
        {subhead ? <p className={styles.subhead}>{subhead}</p> : null}
        {children}
      </div>

      <div className={styles.ctaBar}>
        <div className={styles.ctaBarInner}>
          <button type="button" className={styles.ctaButton} onClick={handleCta} disabled={ctaDisabled}>
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export {styles as primerStyles};
